'use client';

import {Button} from '@pixel-party/Button/v2';
import {CloseIcon, FileUploadOutlinedIcon} from '@pixel-party/Icon/v2';
import {IconButton} from '@pixel-party/IconButton/v2';
import {List, ListItem} from '@pixel-party/List/v2';
import {Typography} from '@pixel-party/Typography/v2';
import {
  type ChangeEvent,
  forwardRef,
  type InputHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from '@/app/forms/constants';
import {formatBytes} from '@/app/forms/utils';
import styles from './FileUpload.module.scss';

export interface FileUploadProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'id'> {
  instruction: string;
  buttonLabel?: string;
  defaultFileName?: string;
  maxFileSizeErrorMessage?: string;
  fileTypeErrorMessage?: string;
  fileAttachedLabel?: string;
  error?: string;
  id?: string;
  maxFiles?: number;
  onFileSelect?: (id: string, files: File[]) => void;
  loading?: boolean;
  required?: boolean;
  value?: File[];
}

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  (props, forwardedRef) => {
    const {t} = useTranslation();

    const {
      instruction,
      buttonLabel = t('fileUpload.buttonLabel'),
      defaultFileName = t('fileUpload.defaultFileName'),
      fileAttachedLabel = t('fileUpload.fileAttachedLabel'),
      fileTypeErrorMessage = t('errors.invalidFileType'),
      maxFileSizeErrorMessage = t('errors.fileTooBig', {
        size: MAX_FILE_SIZE_MB,
      }),
      id: providedId,
      onFileSelect,
      onChange,
      error,
      className,
      maxFiles = 5,
      loading = false,
      required,
      value,
      ...rest
    } = props;

    const generatedId = useId();
    const id = providedId || generatedId;
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [files, setFiles] = useState<File[]>(value || []);
    const [localErrorMessage, setLocalErrorMessage] = useState<string>('');
    const [statusMessage, setStatusMessage] = useState<string>('');

    useEffect(() => {
      const currentFiles = value || [];
      setFiles(currentFiles);

      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        currentFiles.forEach(file => {
          dataTransfer.items.add(file);
        });
        fileInputRef.current.files = dataTransfer.files;
      }
    }, [value]);

    const updateInput = (updatedFiles: File[]) => {
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        updatedFiles.forEach(file => {
          dataTransfer.items.add(file);
        });

        fileInputRef.current.files = dataTransfer.files;

        if (onChange) {
          const event = {
            target: fileInputRef.current,
            currentTarget: fileInputRef.current,
          } as unknown as ChangeEvent<HTMLInputElement>;
          onChange(event);
        }
      }
    };

    const setMergedRef = (element: HTMLInputElement | null) => {
      fileInputRef.current = element;
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else if (forwardedRef && 'current' in forwardedRef) {
        forwardedRef.current = element;
      }
    };

    const handleButtonClick = () => fileInputRef.current?.click();

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newFile = event.target.files?.[0];
      if (!newFile) return;

      const isDuplicate = files.some(
        file => file.name === newFile.name && file.size === newFile.size,
      );
      if (isDuplicate) {
        event.target.value = '';
        return;
      }

      let validationError: string | null = null;
      if (!ACCEPTED_FILE_TYPES.includes(newFile.type)) {
        validationError = fileTypeErrorMessage;
      } else if (newFile.size > MAX_FILE_SIZE_BYTES) {
        validationError = maxFileSizeErrorMessage;
      }

      if (validationError) {
        setLocalErrorMessage(validationError);
        event.target.value = '';
        return;
      }

      setLocalErrorMessage('');
      setStatusMessage('');
      const updatedFiles = [...files, newFile];
      setFiles(updatedFiles);
      updateInput(updatedFiles);

      if (onFileSelect) onFileSelect(id, updatedFiles);
    };

    const handleRemoveFile = (indexToRemove: number) => {
      const fileToRemove = files[indexToRemove];
      const updatedFiles = files.filter((_, index) => index !== indexToRemove);
      setFiles(updatedFiles);
      updateInput(updatedFiles);
      setStatusMessage(
        t('fileUpload.fileRemoved', {fileName: fileToRemove.name}),
      );

      if (onFileSelect) onFileSelect(id, updatedFiles);
    };

    const activeErrorMessage = localErrorMessage || error;

    return (
      <div className={className}>
        <Typography
          use="body2"
          className={styles.paragraph}
        >
          {instruction}
          {required && ' *'}{' '}
        </Typography>
        {activeErrorMessage && (
          <Typography
            use="caption"
            className={styles.inputError}
            role="alert"
          >
            {activeErrorMessage}
          </Typography>
        )}
        {statusMessage && !activeErrorMessage && (
          <Typography
            use="caption"
            className={styles.statusMessage}
            role="status"
          >
            {statusMessage}
          </Typography>
        )}
        {files.length > 0 ? (
          <List className={styles.fileList}>
            {files.map((file, index) => (
              <ListItem
                // biome-ignore lint/suspicious/noArrayIndexKey: safe since order does not change here
                key={`${file.name}-${index}`}
                className={styles.fileItem}
              >
                <div className={styles.fileInfoLeft}>
                  <Typography
                    use="caption"
                    className={styles.fileName}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    use="caption"
                    className={styles.fileAttachedLabel}
                  >
                    {fileAttachedLabel}
                  </Typography>
                </div>
                <div className={styles.fileInfoRight}>
                  <Typography
                    use="caption"
                    className={styles.fileSize}
                  >
                    {formatBytes(file.size)}
                  </Typography>
                  <IconButton
                    size="small"
                    icon={<CloseIcon />}
                    onClick={event => {
                      event.stopPropagation();
                      handleRemoveFile(index);
                    }}
                    className={styles.removeButton}
                    aria-label={t('fileUpload.removeFile', {
                      fileName: file.name,
                    })}
                  />
                </div>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography
            use="caption"
            className={styles.placeholderText}
          >
            {defaultFileName}
          </Typography>
        )}
        <input
          {...rest}
          className={styles.hiddenInput}
          data-testid="file-input"
          type="file"
          id={id}
          ref={setMergedRef}
          onChange={handleFileChange}
          accept={ACCEPTED_FILE_TYPES.join(',')}
        />
        <Button
          variant="tertiary"
          round={true}
          className={styles.button}
          onClick={handleButtonClick}
          disabled={files.length >= maxFiles || loading}
          icon={<FileUploadOutlinedIcon />}
        >
          {buttonLabel}
        </Button>
      </div>
    );
  },
);

FileUpload.displayName = 'FileUpload';
